import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { mdiCog } from '@mdi/js';
import { TranslateService } from '@ngx-translate/core';
import * as React from 'react';

import iocTypes from '../rvirus.imap';
import { lazyInject } from '../rvirus.ioc';

import { ConfigViewPillar, Metric } from './config.insight.model';
import { CustomizeColumnsInsight } from './customize-columns.insight.component';
import { CustomizeColumnsInsightChangeEvent } from './customize-columns.insight.model';
import { CustomizeMetricsInsight } from './customize-metrics.insight.component';
import {
    CustomizeMetricsInsightCanSelectFn,
    CustomizeMetricsInsightChangeEvent,
} from './customize-metrics.insight.model';
import { CustomizePillarsInsight } from './customize-pillars.insight.component';
import {
    CustomizePillarsInsightCanSelectFn,
    CustomizePillarsInsightChangeEvent,
} from './customize-pillars.insight.model';
import {
    CustomizeInsightProps,
    CustomizeInsightState,
} from './customize.insight.model';
import { TranslationKeyMap } from './insight.i18n';
import { MetricPath } from './insight.state';
import { OptionalColumn } from './optional-column.insight.model';
import { PopUp } from './pop-up.component';

class CustomizeInsightImpl extends React.Component<
    CustomizeInsightProps,
    CustomizeInsightState
> {
    @lazyInject(iocTypes.TranslateService.identifier)
    private readonly _translateService: TranslateService;

    private readonly _popUpRef: React.RefObject<PopUp>;

    constructor(props: CustomizeInsightProps) {
        super(props);

        this.state = {};

        this._popUpRef = React.createRef();
    }

    render(): React.ReactNode {
        let count = 0;
        if (this.props.pillars) count++;
        if (this.props.columns) count++;
        if (this.props.metrics) count++;
        const parts = [];
        if (this.props.pillars) {
            parts.push(
                <div
                    className={`col-6 ${count <= 2 ? '' : 'mb-3'}`}
                    key="pillars"
                >
                    <div className="row">
                        <div className="col">
                            <div className="insight-title">
                                {this._translateService.instant(
                                    TranslationKeyMap._801_PILLARS,
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <CustomizePillarsInsight
                                pillars={this.props.pillarsPillars}
                                selection={this.props.pillarsSelection}
                                onChange={this.props.pillarsOnSelectionChange}
                                canSelect={this.props.pillarsCanSelect}
                            />
                        </div>
                    </div>
                </div>,
            );
        }
        if (this.props.columns) {
            parts.push(
                <div
                    className={`col-6 ${count <= 2 ? '' : 'mb-3'}`}
                    key="columns"
                >
                    <div className="row">
                        <div className="col">
                            <div className="insight-title">
                                {this._translateService.instant(
                                    TranslationKeyMap._7511_COLUMNS,
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <CustomizeColumnsInsight
                                columns={this.props.columnsColumns}
                                selection={this.props.columnsSelection}
                                onChange={this.props.columnsOnSelectionChange}
                            />
                        </div>
                    </div>
                </div>,
            );
        }
        if (this.props.metrics) {
            parts.push(
                <div
                    className="col-6"
                    key="metrics"
                >
                    <div className="row">
                        <div className="col">
                            <div className="insight-title">
                                {this._translateService.instant(
                                    TranslationKeyMap._527_METRICS,
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <CustomizeMetricsInsight
                                metrics={this.props.metricsMetrics}
                                pillars={this.props.metricsPillars}
                                selection={this.props.metricsSelection}
                                onChange={this.props.metricsOnSelectionChange}
                                canSelect={this.props.metricsCanSelect}
                            />
                        </div>
                    </div>
                </div>,
            );
        }
        if (parts.length >= 2) {
            parts.splice(
                1,
                0,
                <div
                    className={`insight-separator ${count <= 2 ? '' : 'mb-3'}`}
                    key="separator"
                />,
            );
        }
        return (
            <div className="insight-customize">
                <button
                    className={`btn bg-transparent pr-0 pl-0 ${
                        this.state.isOpen && 'insight-pop-up-open'
                    }`}
                    onClick={() => {
                        if (!this._popUpRef.current) return;
                        if (this._popUpRef.current.isOpen) {
                            this._popUpRef.current.hide();
                        } else {
                            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                            const done = () => this.setState({ isOpen: false });
                            this._popUpRef.current.show().then(
                                () => done(),
                                () => done(),
                            );
                            this.setState({ isOpen: true });
                        }
                    }}
                >
                    <div className="d-inline cmps-icon legacy:cmps-cdk-fill-green-100 cmps-cdk-mr-0dot5">
                        <svg>
                            <path d={mdiCog}></path>
                        </svg>
                    </div>

                    <span>
                        {this._translateService.instant(
                            TranslationKeyMap._11165_CUSTOMIZE,
                        )}
                    </span>
                </button>
                <PopUp
                    target={() =>
                        document.querySelector(
                            '.insight-customize .insight-pop-up-open',
                        )
                    }
                    placement="bottom-left"
                    triangle={true}
                    containerClass="insight-customize insight-pop-up-container"
                    backdrop={true}
                    backdropClass="insight-customize insight-pop-up-backdrop"
                    backdropDismiss={true}
                    relativeTo={() =>
                        document.querySelector('.insight-underlay')
                    }
                    ref={this._popUpRef}
                >
                    <div className="insight-customize insight-pop-up-content insight-scrollable">
                        <div className="col">
                            <div className="row">{parts}</div>
                        </div>
                    </div>
                </PopUp>
            </div>
        );
    }
}

export const CustomizeInsight = CustomizeInsightImpl;

@Component({
    selector: '[rvirus][cmpsInsightCustomize]',
    template: ` <cmps-insight-customize
        [pillars]="pillars"
        [pillarsPillars]="pillarsPillars"
        [pillarsSelection]="pillarsSelection"
        (pillarsOnSelectionChange)="pillarsSelectionChange.emit($event)"
        [pillarsCanSelect]="pillarsCanSelect"
        [columns]="columns"
        [columnsColumns]="columnsColumns"
        [columnsSelection]="columnsSelection"
        (columnsOnSelectionChange)="columnsSelectionChange.emit($event)"
        [metrics]="metrics"
        [metricsMetrics]="metricsMetrics"
        [metricsPillars]="metricsPillars"
        [metricsSelection]="metricsSelection"
        (metricsOnSelectionChange)="metricsSelectionChange.emit($event)"
        [metricsCanSelect]="metricsCanSelect"
    ></cmps-insight-customize>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class CustomizeInsightComponent {
    @Input() pillars: boolean;
    @Input() pillarsPillars: ConfigViewPillar[];
    @Input() pillarsSelection: ConfigViewPillar[];
    @Output()
    pillarsSelectionChange: EventEmitter<CustomizePillarsInsightChangeEvent> =
        new EventEmitter();

    @Input() pillarsCanSelect: CustomizePillarsInsightCanSelectFn;
    @Input() columns: boolean;
    @Input() columnsColumns: OptionalColumn[];
    @Input() columnsSelection: OptionalColumn[];
    @Output()
    columnsSelectionChange: EventEmitter<CustomizeColumnsInsightChangeEvent> =
        new EventEmitter();

    @Input() metrics: boolean;
    @Input() metricsMetrics: Metric[];
    @Input() metricsPillars: ConfigViewPillar[];
    @Input() metricsSelection: MetricPath[];
    @Output()
    metricsSelectionChange: EventEmitter<CustomizeMetricsInsightChangeEvent> =
        new EventEmitter();

    @Input() metricsCanSelect: CustomizeMetricsInsightCanSelectFn;
}
